import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, QueryList, ViewChild, ViewChildren, ViewContainerRef } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule, MatIconButton } from '@angular/material/button';
import { MatFormField, MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { Router } from '@angular/router';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { SelectionModel } from '@angular/cdk/collections';
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';
import { DataTableColumn } from './DataTableColumn.model';
import { DataTableFilter } from './filters/components/DataTableFilter';
import { ActiveEventMessage, DataTableFilterMessageService } from './filters/components/data-table-filter-message-service';
import { DataTableFilterPillComponent, DataTableFilterPillState } from './filters/components/data-table-filter-pill/data-table-filter-pill.component';
import { SortOrderType, SortParams } from '../../search/SearchParams';
import { PolicyPrice } from '../../../feature/policies/models/Policy';
import { NavService } from '../../../layout/nav/nav.service';
import { DataTableFilterComponent } from './filters/components/data-table-filter/data-table-filter.component';
import { PillComponent } from '../pill/pill.component';
import { SearchService } from '../../search/search.service';

@Component({
  selector: 'app-data-table',
  templateUrl: './data-table.component.html',
  styleUrls: ['./data-table.component.scss'],
  standalone: true,
  imports:[
    CommonModule,
    MatIconModule,
    MatFormField,
    MatSortModule,
    MatPaginator,
    MatTableModule,
    PillComponent,
    MatProgressSpinner,
    MatInputModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    FormsModule,
    MatButtonModule,
    MatIconButton,
    MatCheckboxModule,
    DataTableFilterPillComponent,
    DataTableFilterComponent
  ]
})
export class DataTableComponent implements OnInit, AfterViewInit, OnDestroy {

  public rows = new MatTableDataSource<any>();
  public columns = [];
  public selection = new SelectionModel<any>(true, []);

  @Input() component!: string;
  @Input() set dataSource(value: MatTableDataSource<any>) {
    this._dataSource = value;
    this.rows = value;
    this.updateSort();
  };

  @Input() displayedColumns!: DataTableColumn[];
  @Input() tableLength?: number;
  @Input() pageSize?: number;
  @Input() pageNumber?: number;
  @Input() disablePageSizeSelector?: boolean = false
  @Input() searchTimeoutMs?: number = 500;
  @Input() filterDefinitions!: DataTableFilter[];
  @Input() displayLoadingIndicator!: BehaviorSubject<boolean>;
  @Input() getCalculatedActionButtonText?: (column: any) => string | null;
  @Input() actionButtonText?: string;
  @Input() getActionButtonIcon?: (column: any) => string;
  @Input() getActionButtonColor?: (column: any) => string;
  @Input() showSearch?: boolean = true;
  @Input() navigateOnRowClick?: boolean = true;
  @Input() showPaginator: boolean = false;
  @Input() hidePointer: boolean = false;
  @Input() disableSorting: boolean = false;
  @Input() getStatusColor?: (status: string) => string;
  @Input() rowClicked?: (row: any) => void;
  @Input() allowPropertyReservationUpdateBoolean: boolean = false;

  @Input() initialPageSize?: BehaviorSubject<number>;
  @Input() initialPageNumber?: BehaviorSubject<number>;
  @Input() initialSort?: BehaviorSubject<Sort | undefined>;
  @Input() initialKeyword?: BehaviorSubject<string>;

  @Output() pageEvent: EventEmitter<PageEvent> = new EventEmitter<PageEvent>();
  @Output() searchEvent: EventEmitter<string> = new EventEmitter<string>();
  @Output() sortEvent: EventEmitter<SortParams> = new EventEmitter<SortParams>();
  @Output() actionClickedEvent: EventEmitter<any> = new EventEmitter<any>();
  @Output() editVerificationPackageClickEvent = new EventEmitter<any>();
  @Output() updatePrimaryVerificationPackageClickEvent = new EventEmitter<any>();
  @Output() deleteVerificationPackageClickEvent = new EventEmitter<any>();
  @Output() editPolicyUnderwriterClickedEvent: EventEmitter<any> = new EventEmitter<any>();
  @Output() deletePolicyUnderwriterClickedEvent: EventEmitter<any> = new EventEmitter<any>();
  @Output() deleteVerificationCheckTypeClickedEvent: EventEmitter<any> = new EventEmitter<any>();
  @Output() editUnderwriterClickedEvent: EventEmitter<any> = new EventEmitter<any>();
  @Output() deleteUnderwriterClickedEvent: EventEmitter<any> = new EventEmitter<any>();
  @Output() editCurrencyClickedEvent: EventEmitter<any> = new EventEmitter<any>();
  @Output() deleteCurrencyClickedEvent: EventEmitter<any> = new EventEmitter<any>();
  @Output() editBadGuestClickedEvent: EventEmitter<any> = new EventEmitter<any>();
  @Output() deleteBadGuestClickedEvent: EventEmitter<any> = new EventEmitter<any>();
  @Output() massUpdateReservationStatusEvent: EventEmitter<any> = new EventEmitter<any>();
  @Output() massUpdateReservationPolicyEvent: EventEmitter<any> = new EventEmitter<any>();
  @Output() massUpdatePropertyStatusEvent: EventEmitter<any> = new EventEmitter<any>();
  @Output() massUpdatePropertyPolicyEvent: EventEmitter<any> = new EventEmitter<any>();

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort: MatSort = new MatSort();
  @ViewChildren('filters') filters!: QueryList<ViewContainerRef>;
  @ViewChild('searchKeyword') searchKeywordInput!: ElementRef;

  private destroy$ = new Subject<void>();

  _dataSource!: MatTableDataSource<any>;
  displayedColumnNames: string[] = [];
  searchTimeout: any;
  searchKeyword: string = '';
  filterPills = new Map<string, DataTableFilterPillState>();
  selectedRowIndex: any;
  selectedOrganizationName!: string;
  orgFilterActive: boolean = false;

  constructor(
    private router: Router,
    private filterMessageService: DataTableFilterMessageService,
    private snackbar: MatSnackBar,
    private navService: NavService,
    private searchService: SearchService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    if (this._dataSource == null) {
      this.dataSource = new MatTableDataSource();
    }

    this.navService.selectedOrg$.pipe(takeUntil(this.destroy$)).subscribe((organization: any) => {
      if (organization) {
        this.orgFilterActive = true;
        this.selectedOrganizationName = organization.legalName;
      } else {
        this.orgFilterActive = false;
      }
    });

    this.subscribeToFilterActiveEvent();
    this.initTableColumns();
  }

  ngAfterViewInit() {
    this._dataSource.sort = this.sort;
    this._dataSource.paginator = this.paginator;

    this.initialPageSize?.pipe(takeUntil(this.destroy$)).subscribe(pageSize => {
      this.pageSize = pageSize;
      this.paginator.pageSize = pageSize;
      this.cdr.detectChanges();
    });

    this.initialPageNumber?.pipe(takeUntil(this.destroy$)).subscribe(pageNumber => {
      if (pageNumber != 1) {
        this.pageNumber = pageNumber;
        this.paginator.pageIndex = pageNumber;
      } else {
        this.pageNumber = 1;
      }
      this.cdr.detectChanges();
    });

    this.initialKeyword?.pipe(takeUntil(this.destroy$)).subscribe(keyword => {
      this.searchKeyword = keyword;
      this.cdr.detectChanges();
    });

    this.initialSort?.pipe(takeUntil(this.destroy$)).subscribe(sort => {
      if (!sort) {
        return;
      }

      this.setSort(sort);
      this.cdr.detectChanges();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async loadFromPolicyPriceRate(priceRate: PolicyPrice) {
    this.onRowClick(priceRate);
  }

  initTableColumns(): void {
    if (this.displayedColumns === null) {
      this.displayedColumns = [];
    }
    this.displayedColumnNames = this.displayedColumns.map(c => c.column);

    if (
      this.component != 'Organizations' && 
      this.component != 'Claims' && 
      this.component != 'Verification Packages' && 
      this.component != 'Policy Prices' && 
      this.component != 'Property Claims' &&
      this.component != 'Insurance Policies' &&
      this.component != 'Countries' &&
      this.component != 'Policy Underwriters' &&
      this.component != 'Check Types' &&
      this.component != 'Currencies' && 
      this.component != 'Reservation Properties'
    ) {
      this.displayedColumnNames = ['select'].concat(this.displayedColumnNames);
    }
  }

  subscribeToFilterActiveEvent() {
    this.filterMessageService.activeEvent.pipe(takeUntil(this.destroy$)).subscribe((event: ActiveEventMessage) => {
      if (!this.filterDefinitions) {
        return;
      }

      this.resetPaginator(1, true);
      if (event.active) {
        let pillText = this.filterDefinitions.find(definition => definition.id == event.filterId)?.shortName || '';
        if (event.value) {
          pillText = pillText + " : " + event.value;
        }
        this.filterPills.set(event.filterId, { filterId: event.filterId, text: pillText });
      } else {
        this.filterPills.delete(event.filterId);
      }
      this.cdr.detectChanges();
    });
  }

  updateSort() {
    if (!this._dataSource) {
      return;
    }

    /*INSIGHT: This disables the data source's internal sorting.
    * We are getting pre-sorted data from the API and do not need to sort it again. */
    this._dataSource.sortData = (data: any[], _: MatSort) => data;
    this._dataSource.sort = this.sort;
  }

  onFilterPillCloseEvent(filterId: string) {
    this.filterMessageService.onClearClicked(filterId);
  }

  onSearchEvent(event: Event) {
    this.paginator.pageIndex = 0;
    this.pageNumber = 1;

    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = null;
    }

    this.searchTimeout = setTimeout(() => {
      const keyword = (event.target as HTMLInputElement).value;
      this.searchKeyword = keyword;
      this.searchEvent.emit(keyword);
    }, this.searchTimeoutMs);
  }

  onDeleteKeyUp() {
    if (this.searchKeyword == '') {
      this.searchEvent.emit('');
    }
  }

  clearSearch() {
    this.searchService.removeTextQuery();
    this.searchKeyword = '';
    this.resetPaginator(1, true);
    this.searchEvent.emit('');
  }

  onPageEvent(event: PageEvent): void {
    this.pageNumber = event.pageIndex + 1;
    this.pageEvent.emit(event);
  }

  onSortEvent(event: any) {
    const { active: sortField, direction } = event;

    let sortOrder: SortOrderType;
    if (direction === 'asc') {
      sortOrder = 1;
    } else {
      sortOrder = -1;
    }

    this.sortEvent.emit(({ sortField, sortOrder }));
  }

  setSort(sort: Sort) {
    this.sort.active = sort.active;
    this.sort.direction = sort.direction;
    this.sort._stateChanges.next();
  }

  onRowClick(row: any): void {
    if (this.navigateOnRowClick) {
      switch(this.component) {
        case 'Claims':
          this.router.navigate([`/claims/detail/${row['id']}`], {
            state: { data: row }
          });
          break;
        case 'Insurance Policies':
          this.router.navigate([`/policies/detail/${row['id']}`]);
          break;
        case 'Property Claims':
          this.router.navigate([`/claims/detail/${row['id']}`]);
          break;
        default:
          this.router.navigate([`/${this.component.charAt(0).toLowerCase() + this.component.slice(1)}/detail/${row['id']}`]);
      }
    }
  }

  onActionButtonClick(event: MouseEvent, row: any): void {
    event.stopPropagation();
    this.actionClickedEvent.emit(row);
  }

  onEditVerificationPackageButtonClick(event: MouseEvent, row: any) {
    event.stopPropagation();
    this.editVerificationPackageClickEvent.emit(row);
  }

  onUpdatePrimaryVerificationPackageClick(event: MouseEvent, row: any) {
    event.stopPropagation();
    this.updatePrimaryVerificationPackageClickEvent.emit(row);
  }

  onDeleteVerificationPackageButtonClick(event: MouseEvent, row: any) {
    event.stopPropagation();
    this.deleteVerificationPackageClickEvent.emit(row);
  }

  onEditPolicyUnderwriterButtonClick(event: MouseEvent, row: any) {
    event.stopPropagation();
    this.editPolicyUnderwriterClickedEvent.emit(row);
  }

  onDeletePolicyUnderwriterButtonClick(event: MouseEvent, row: any) {
    event.stopPropagation();
    this.deletePolicyUnderwriterClickedEvent.emit(row);
  }

  onDeleteVerificationCheckTypeButtonClick(event: MouseEvent, row: any) {
    event.stopPropagation();
    this.deleteVerificationCheckTypeClickedEvent.emit(row);
  }

  onEditUnderwriterButtonClick(event: MouseEvent, row: any) {
    event.stopPropagation();
    this.editUnderwriterClickedEvent.emit(row);
  }

  onDeleteUnderwriterButtonClick(event: MouseEvent, row: any) {
    event.stopPropagation();
    this.deleteUnderwriterClickedEvent.emit(row);
  }

  onEditCurrencyButtonClick(event: MouseEvent, row: any) {
    event.stopPropagation();
    this.editCurrencyClickedEvent.emit(row);
  }

  onDeleteCurrencyButtonClick(event: MouseEvent, row: any) {
    event.stopPropagation();
    this.deleteCurrencyClickedEvent.emit(row);
  }

  onEditBadGuestButtonClick(event: MouseEvent, row: any) {
    event.stopPropagation();
    this.editBadGuestClickedEvent.emit(row);
  }

  onDeleteBadGuestButtonClick(event: MouseEvent, row: any) {
    event.stopPropagation();
    this.deleteBadGuestClickedEvent.emit(row);
  }

  onReservationFormSelectProperty(event: MouseEvent, row: any) {
    event.stopPropagation();
    this.actionClickedEvent.emit(row);
  }

  onGoToPageClick() {
    const max = Math.trunc(Math.ceil(this.paginator.length / this.paginator.pageSize));
    if (this.pageNumber && this.pageNumber < 1) {
      this.resetPaginator(1, true);
      this.displayInvalidPaginatorPageIndexError();
    } else if (this.pageNumber && this.pageNumber > max) {
      this.resetPaginator(max, false);
      this.displayInvalidPaginatorPageIndexError();
    } else {
      if (this.pageNumber) {
        this.paginator.pageIndex = this.pageNumber - 1;
        this.paginator.page.next({
          pageIndex: this.pageNumber - 1,
          pageSize: this.paginator.pageSize,
          length: this.paginator.length
        });
      }
    }
  }

  resetPaginator(pageNumber: number, goToFirstPage: boolean) {
    this.pageNumber = pageNumber;

    if (this.paginator) {
      if (goToFirstPage) {
        this.paginator.firstPage();
      } else {
        this.paginator.lastPage();
      }
    }
  }

  displayInvalidPaginatorPageIndexError() {
    this.snackbar.open('Invalid Page Number', 'OK', { duration: 3000 });
  }

  getColor(status: string) {
    if (this.getStatusColor) {
      return this.getStatusColor(status);
    } else {
      return '#d3d3d3';
    }
  }

  public isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.rows.data.length;
    return numSelected === numRows;
  }

  public masterToggle() {
    this.isAllSelected() ? this.selection.clear() : this.rows.data.forEach(row => this.selection.select(row));
  }

  public checkboxLabel(row?: any): string {
    return (!row)
      ? `${this.isAllSelected() ? 'select' : 'deselect'} all`
      : `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${row.position + 1}`;
  }

  useMassStatusSelection(component: string, event: MouseEvent, selection: any) {
    event.stopPropagation();
    switch(component) {
      case 'Properties': 
        this.massUpdatePropertyStatusEvent.emit(selection);
        break;
      case 'Reservations': 
        this.massUpdateReservationStatusEvent.emit(selection);
        break;
    }
    setTimeout(() => { this.selection.clear(); }, 3000);
  }

  useMassPolicySelection(component: string, event: MouseEvent, selection: any) {
    event.stopPropagation();
    switch(component) {
      case 'Properties': 
        this.massUpdatePropertyPolicyEvent.emit(selection);
        break;
      case 'Reservations': 
        this.massUpdateReservationPolicyEvent.emit(selection);
        break;
    }
    setTimeout(() => { this.selection.clear(); }, 3000);
  }
}